import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Schedule as ScheduleIcon,
  Timer as TimerIcon,
} from "@mui/icons-material";
import { alpha, Box, Chip, Drawer, IconButton, Paper, Stack, Typography } from "@mui/material";
import { type FC, memo } from "react";
import { Link } from "react-router";
import { LoadingBox } from "@/components/ui";
import { useApiQuery } from "../../hooks/use-api-query";
import { BRAND_PRIMARY, theme } from "../../theme/theme";
import type { CheckRun, CheckScheduleRun, DailyStatus } from "../../types";
import { UPTIME_COLORS } from "../../utils/uptime-colors";
import { RunStatusChip } from "../run-status-chip";

interface DayRunsDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedDay: DailyStatus | null;
  scheduleId: string;
  scheduleName: string;
  checkId?: string;
  checkName?: string;
}

interface ScheduleRunsResponse {
  runs: CheckScheduleRun[];
  total: number;
}

export const DayRunsDrawer: FC<DayRunsDrawerProps> = memo(
  ({ open, onClose, selectedDay, scheduleId, scheduleName, checkId, checkName }) => {
    const dateStr = selectedDay?.date;

    // Fetch schedule runs for this day
    const { data: scheduleRunsData, isLoading: loadingScheduleRuns } =
      useApiQuery<ScheduleRunsResponse>(`/check-schedules/${scheduleId}/runs?limit=50`, {
        enabled: open && !!scheduleId && !checkId,
      });

    // Fetch check runs for this day (if viewing a specific check)
    const { data: checkRunsData, isLoading: loadingCheckRuns } = useApiQuery<{
      runs: CheckRun[];
      total: number;
    }>(`/checks/${checkId}/runs?limit=50`, {
      enabled: open && !!checkId,
    });

    const isLoading = loadingScheduleRuns || loadingCheckRuns;

    // Filter runs for the selected date
    const filteredScheduleRuns = scheduleRunsData?.runs.filter((run) => {
      if (!dateStr) return false;
      const runDate = new Date(run.created_at).toISOString().split("T")[0];
      return runDate === dateStr;
    });

    const filteredCheckRuns = checkRunsData?.runs.filter((run) => {
      if (!dateStr) return false;
      const runDate = new Date(run.created_at).toISOString().split("T")[0];
      return runDate === dateStr;
    });

    const runs = checkId ? filteredCheckRuns : filteredScheduleRuns;

    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    const formatTime = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const statusColor = (status: string) =>
      UPTIME_COLORS[status as keyof typeof UPTIME_COLORS] ?? UPTIME_COLORS.no_data;

    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500, md: 600 },
            p: 0,
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2.5,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: alpha(statusColor(selectedDay?.status || "unknown"), 0.05),
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ color: "text.primary", mb: 0.5 }}>
                  {checkName || scheduleName}
                </Typography>
                {dateStr && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {formatDate(dateStr)}
                  </Typography>
                )}
              </Box>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>

            {selectedDay && (
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} />
                  <Typography variant="body2" fontWeight={500}>
                    {selectedDay.passed_runs} passed
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CancelIcon sx={{ color: "error.main", fontSize: 18 }} />
                  <Typography variant="body2" fontWeight={500}>
                    {selectedDay.failed_runs} failed
                  </Typography>
                </Paper>
              </Stack>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {isLoading ? (
              <LoadingBox size={32} sx={{ py: 8, p: 0 }} />
            ) : runs && runs.length > 0 ? (
              <Stack spacing={1.5}>
                {runs.map((run) => {
                  const runUrl = checkId
                    ? `/checks/${checkId}?tab=runs&runId=${run.id}`
                    : `/check-schedules/${scheduleId}?tab=runs&run_id=${run.id}`;

                  return (
                    <Paper
                      key={run.id}
                      component={Link}
                      to={runUrl}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        transition: "all 0.15s ease",
                        textDecoration: "none",
                        display: "block",
                        cursor: "pointer",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: alpha(BRAND_PRIMARY, 0.02),
                        },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <RunStatusChip status={run.run_status} size="small" />
                          <Typography variant="body2" sx={{ color: "grey.800" }}>
                            {formatTime(run.created_at)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {"duration_seconds" in run && run.duration_seconds && (
                            <Chip
                              icon={<TimerIcon sx={{ fontSize: 14 }} />}
                              label={`${run.duration_seconds}s`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem" }}
                            />
                          )}
                          {"duration_ms" in run && run.duration_ms && (
                            <Chip
                              icon={<TimerIcon sx={{ fontSize: 14 }} />}
                              label={`${run.duration_ms}ms`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem" }}
                            />
                          )}
                          {"trigger" in run && (
                            <Chip
                              icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                              label={run.trigger}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem" }}
                            />
                          )}
                          <OpenInNewIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                        </Stack>
                      </Stack>

                      {"total_checks" in run && (
                        <Stack direction="row" spacing={2} sx={{ mt: 1.5, pl: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {run.passed_checks}/{run.total_checks} checks passed
                          </Typography>
                        </Stack>
                      )}

                      {"error_message" in run && run.error_message && (
                        <Box
                          sx={{
                            mt: 1.5,
                            p: 1.5,
                            bgcolor: alpha(theme.palette.error.main, 0.05),
                            borderRadius: 1,
                            borderLeft: "3px solid",
                            borderLeftColor: "error.main",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "error.dark",
                              fontFamily: "monospace",
                            }}
                          >
                            {run.error_message}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  color: "text.disabled",
                }}
              >
                <ScheduleIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1">No runs on this day</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
    );
  },
);

DayRunsDrawer.displayName = "DayRunsDrawer";
